import React from "react";
import { Box, Button, Flex, Heading, Menu, MenuButton, MenuItem, MenuList, Text, useToast, chakra } from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import { H2Dashboard } from "components/text/headings";
import ActiveAuditSummary from "../Audit/components/ActiveAuditSummary";
import CompletedAuditList from "../Audit/components/CompletedAuditList";
import { useGetAllAudit, useGetStagesOfRound } from "../../../hooks/query";
import { showErrorToast } from "../../../utils";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../config";
import LocalSpinner from "../../../components/LocalSpinner";
import useAirUserRoles from "../../../utils/AuthUtils";

const StartAuditPanel = ({ auditStages, activeAuditStageNames }) => {
  const navigate = useNavigate();
  const availableStages = auditStages.filter((s) => !activeAuditStageNames.has(s.name));

  if (availableStages.length === 0) return null;

  return (
    <Box
      w="50%"
      border="1px solid"
      borderColor="lime"
      borderRadius="lg"
      bg="lightLime"
      px={6}
      pt={0}
      pb={8}
      position="relative"
      overflow="hidden"
    >
      <chakra.svg
        position="absolute"
        right="0"
        bottom="0"
        width="220px"
        height="180px"
        viewBox="0 0 175 114"
        preserveAspectRatio="xMaxYMax meet"
        fill="none"
        pointerEvents="none"
      >
        <path d="M31.2441 113.272C31.2066 113.31 31.1694 113.349 31.1318 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z" fill="#85C22A" fillOpacity="0.15" />
        <path d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z" fill="#85C22A" fillOpacity="0.15" />
        <path d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z" fill="#85C22A" fillOpacity="0.15" />
      </chakra.svg>
      <Heading fontSize="heading.m" fontWeight="700" color="darkTeal" mb={3}>
        New Audit
      </Heading>
      <Text fontSize="body.m" color="gray.600" mb={8} position="relative" zIndex={1}>
        Start an internal audit to review and validate disclosures before finalising the round.
      </Text>
      {availableStages.length === 1 ? (
        <Button
          variant="primary"
          borderRadius="full"
          position="relative"
          zIndex={1}
          onClick={() => navigate(`${routes.issbStartAudit}?stageId=${availableStages[0].id}&stageName=${availableStages[0].name}`)}
        >
          Start {availableStages[0].name}
        </Button>
      ) : (
        <Menu>
          <MenuButton
            as={Button}
            variant="primary"
            borderRadius="full"
            position="relative"
            zIndex={1}
            rightIcon={<CaretDown size={14} weight="bold" />}
          >
            Start audit
          </MenuButton>
          <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
            {availableStages.map((stage) => (
              <MenuItem
                key={stage.id}
                fontSize="sm"
                onClick={() => navigate(`${routes.issbStartAudit}?stageId=${stage.id}&stageName=${stage.name}`)}
              >
                Start {stage.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </Box>
  );
};

const AuditDashboard = () => {
  const toast = useToast();
  const { isController } = useAirUserRoles();

  const [auditData, setAuditData] = React.useState([]);

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  const { isLoading } = useGetAllAudit(questionnaireId, {
    onSuccess: (data) => {
      setAuditData(data);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    staleTime: 0,
    enabled: true,
    refetchInterval: 5000,
  });

  const { data: stagesData } = useGetStagesOfRound(questionnaireId, {
    enabled: !!questionnaireId,
  });

  const auditStages = (stagesData || []).filter((s) => s.stageType === "AUDIT");
  const activeAuditStageNames = new Set(
    auditData.filter((a) => a.isActive).map((a) => a.stageName)
  );

  return isLoading ? (
    <LocalSpinner />
  ) : (
    <Flex direction="column" gap={6}>
      {isController() && (
        <StartAuditPanel auditStages={auditStages} activeAuditStageNames={activeAuditStageNames} />
      )}

      <H2Dashboard>Active Audits</H2Dashboard>

      {auditData.filter((audit) => audit?.isActive).length === 0 ? (
        <Text color="gray.400" fontSize="body.s" mb={4}>
          No active audits at the moment
        </Text>
      ) : (
        auditData
          .filter((audit) => audit?.isActive)
          .map((audit) => <ActiveAuditSummary key={audit.id} auditData={audit} auditStages={auditStages} />)
      )}

      <CompletedAuditList auditList={auditData.filter((audit) => audit?.isActive === false)} />
    </Flex>
  );
};

export default AuditDashboard;
